'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type SyncError = {
  url?: string | null;
  httpStatus?: number | string | null;
  rawRequest?: unknown;
  rawResponse?: unknown;
};

type SyncErrorDetailsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  syncError: SyncError | null | undefined;
};

export function SyncErrorDetailsDialog({ open, onOpenChange, syncError }: SyncErrorDetailsDialogProps) {
  const { t } = useTranslation('orders');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('syncErrorDetails')}</DialogTitle>
          <DialogDescription>{t('syncErrorDetailsDescription')}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto overscroll-contain">
          <div className="space-y-4 p-1">
            {syncError?.url && (
              <div>
                <p className="text-sm font-medium">{t('syncErrorUrl')}</p>
                <p className="text-muted-foreground break-all text-sm">{syncError.url}</p>
              </div>
            )}
            {syncError?.httpStatus && (
              <div>
                <p className="text-sm font-medium">{t('syncErrorHttpStatus')}</p>
                <p className="text-muted-foreground text-sm">{syncError.httpStatus}</p>
              </div>
            )}
            {!!syncError?.rawRequest && (
              <div>
                <p className="text-sm font-medium">{t('syncErrorRawRequest')}</p>
                <pre className="bg-muted max-h-48 overflow-auto rounded p-2 text-xs">
                  {typeof syncError.rawRequest === 'object'
                    ? JSON.stringify(syncError.rawRequest, null, 2)
                    : String(syncError.rawRequest)}
                </pre>
              </div>
            )}
            {!!syncError?.rawResponse && (
              <div>
                <p className="text-sm font-medium">{t('syncErrorRawResponse')}</p>
                <pre className="bg-muted max-h-48 overflow-auto rounded p-2 text-xs">
                  {typeof syncError.rawResponse === 'object'
                    ? JSON.stringify(syncError.rawResponse, null, 2)
                    : String(syncError.rawResponse)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
